/* eslint-disable react/no-unknown-property */
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import React, { useState, useRef, Suspense } from "react";
import {
  OrbitControls,
  PresentationControls,
  TransformControls,
  AccumulativeShadows,
  SoftShadows,
  RandomizedLight,
  useProgress,
  Environment,
  Lightformer,
  Instances,
  Instance,
  useGLTF,
  Cloud,
  Center,
  Line,
  useCursor,
} from "@react-three/drei";
import { useModelCompileTick } from "@/stores/modelCompileStore";
import Placeholder from "./Placeholder";
import CaiLogo from "./CaiLogo";
import CaiLogoLamina from "./CaiLogoLamina";
import Clouds from "./Clouds";
import NavBlocks from "./NavBlocks";
import Text3D from "./Text3D";
import Arrow3D from "./Arrow3D";
import BakedShadows from "./BakedShadows";
import ShadowBakeExporter from "./ShadowBakeExporter";
import Effects from "./Effects";
import { Selection } from "@react-three/postprocessing";
import ArchSection from "./arch/ArchSection";
import VArchSection from "./varch/VArchSection";
import ProductSection from "./product/ProductSection";
import DragIcon from "@/components/ui/dragIcon";
import ProjectsSection from "./projects/ProjectsSection";
import Avatar from "./avatar/Avatar";
import GroundPlane from "./avatar/GroundPlane";
import SpeechBubble from "./avatar/SpeechBubble";
import useAvatarMovement from "./avatar/useAvatarMovement";
import useZoneDetection from "./avatar/useZoneDetection";
import { useChatStore } from "@/stores/chatStore";
import { useProjectFocus } from "@/stores/projectFocusStore";
import { FOCUS_RIG_TARGET } from "./focusUtils";
import DebugColliders from "./avatar/DebugColliders";
import DebugZones from "./avatar/DebugZones";
import PathLine from "./avatar/PathLine";

// Re-bakes AccumulativeShadows after streamed-in models appear. The initial
// bake runs in the first frames, before those models exist, so without this
// they never cast shadows. Every model pings the compile store when its
// shaders are ready; 1.2s after the last ping we bump state so
// AccumulativeShadows re-renders - its own no-deps layout effect then runs
// the reset+bake exactly as it does on any other re-render. Event-driven,
// not wall-clock: fixed timers either fired before slow models arrived or
// dragged bake stalls seconds after the scene looked settled.
// Shadow mode switch:
//   (default)      baked  - static pre-baked lightmap (BakedShadows), no
//                           startup bake cost
//   ?shadows=live  live   - runtime AccumulativeShadows; used by
//                           scripts/bake-shadows.mjs to re-export the
//                           lightmap after scene changes, and for visual
//                           comparison against the baked result
//   ?shadows=off   off    - no ground shadows (perf measurement)
const shadowsParam =
  typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("shadows")
    : null;
const SHADOW_MODE =
  shadowsParam === "off" ? "off" : shadowsParam === "live" ? "live" : "baked";
// ?shadowres=2048|4096 - lightmap resolution for live mode / export.
const SHADOW_RES =
  Number(
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("shadowres")
      : 0,
  ) || 1024;

function useShadowRebake() {
  const tick = useModelCompileTick((s) => s.tick);
  const [, setRender] = useState(0);

  React.useEffect(() => {
    // Only the live AccumulativeShadows needs re-baking as models stream in.
    if (SHADOW_MODE !== "live" || tick === 0) return undefined;
    const timer = setTimeout(() => setRender((r) => r + 1), 1200);
    return () => clearTimeout(timer);
  }, [tick]);
}

export default function Experience() {
  // const { camera, gl } = useThree();
  const cubeRef = useRef();
  const controlsRef = useRef();
  const liveShadowsRef = useRef();
  useShadowRebake();
  // const { showColliders, showZones } = useControls("Avatar Debug", {
  //   showColliders: { value: false, label: "Show Colliders" },
  //   showZones: { value: false, label: "Show Zones" },
  // });
  const showColliders = false;
  const showZones = false;
  const movement = useAvatarMovement();
  const currentZone = useZoneDetection(movement.position);
  const { toggleChat, setCurrentZone } = useChatStore();
  const focusBusy = useProjectFocus(
    (s) => Boolean(s.focused) || s.restoring,
  );

  // Sync zone detection with chat store
  React.useEffect(() => {
    setCurrentZone(currentZone);
  }, [currentZone, setCurrentZone]);

  useFrame((state, delta) => {
    // rotate camera around in a circle
    // const angle = state.clock.elapsedTime;
    // state.camera.position.x = Math.sin(angle) * 8;
    // state.camera.position.z = Math.cos(angle) * 8;
    // state.camera.lookAt(0, 0, 0);
    // rotate objects
    // cubeRef.current.rotation.y += delta;
    // groupRef.current.rotation.y += delta;
  });

  const Grids = ({ number = 42, lineWidth = 0.026, height = 0.5 }) => (
    // Renders a grid and crosses as instances
    <Instances position={[0, -0.01, 0]} limit={5000}>
      <planeGeometry args={[lineWidth, height]} />
      <meshBasicMaterial color="#999" />b
      <gridHelper
        args={[100, 100, "#424242", "#272727"]}
        position={[0, -0.02, 0]}
      />
    </Instances>
  );

  // set 3D CAI logo position relative to viewport
  const { width, height } = useThree((state) => state.viewport);

  // Hover state
  const [hovered, hover] = useState(false);
  useCursor(hovered);

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        makeDefault
        // Disabled while the focus camera owns the view (including the
        // fly-back); user drag would fight the CameraRig damp loop.
        enabled={!focusBusy}
        // Seeds each new controls instance (they remount on camera swaps)
        // with the rig's current look-at, so mounting never snaps the view
        // back to the origin.
        target={FOCUS_RIG_TARGET}
        enableZoom={true}
        enableRotate={true}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 2 - Math.PI / 36} // 5 degrees above horizon
        minDistance={5}
        maxDistance={50}
        mouseButtons={{
          LEFT: THREE.MOUSE.PAN,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.ROTATE,
        }}
        enableTouchPanning={true}
        enablePan={true}
        // enableDamping={true}
        // dampingFactor={0.05}
        panSpeed={1.5}
        touches={{
          ONE: THREE.TOUCH.PAN,
          TWO: THREE.TOUCH.DOLLY_ROTATE,
        }}
      // touchPanSpeed={0.5}
      />
      <group>
        <directionalLight position={[-20, 30, 10]} intensity={4.5} />
        <ambientLight intensity={0.2 * Math.PI} />
        {/* Soft shadows */}
        {/* <SoftShadows size={25} samples={10} focus={0} /> */}
        {/* No `temporal`: it spreads the 30 bake passes over 30 visible
            frames, exposing half-baked shadow states (ghost projections)
            during load. Blocking bake happens once at mount instead. */}
        {SHADOW_MODE === "baked" && (
          <Suspense fallback={null}>
            <BakedShadows />
          </Suspense>
        )}
        {SHADOW_MODE === "live" && (
        <AccumulativeShadows
          ref={liveShadowsRef}
          resolution={SHADOW_RES}
          frames={30}
          color={"#1d121e"}
          colorBlend={5}
          toneMapped={true}
          alphaTest={0.9}
          opacity={1}
          scale={100}
          position={[0, -0.01, 0]}
        >
          <RandomizedLight
            amount={4}
            radius={5}
            ambient={0.5}
            intensity={1}
            position={[-20, 30, 10]}
            // Shadow camera spans [-size, size] around the origin; the
            // projects zone reaches ~(45, 35), so 15 left it uncovered.
            size={55}
            mapSize={1024}
            bias={0.0001}
          />
        </AccumulativeShadows>
        )}
        {SHADOW_MODE === "live" && (
          <ShadowBakeExporter shadowsRef={liveShadowsRef} />
        )}
      </group>
      <Suspense fallback={null}>
        <Environment
          preset="dawn"
          // background
          roughness={0.8}
          environmentIntensity={0.1}
          resolution={256}
          blur={1}
        >
          <group rotation={[-Math.PI / 2, 0, 0]}>
            <Lightformer
              intensity={4}
              rotation-x={Math.PI / 2}
              position={[0, 5, -9]}
              scale={[10, 10, 1]}
            />
            {[2, 0, 2, 0, 2, 0, 2, 0].map((x, i) => (
              <Lightformer
                key={i}
                form="circle"
                intensity={4}
                rotation={[Math.PI / 2, 0, 0]}
                position={[x, 4, i * 4]}
                scale={[4, 1, 1]}
              />
            ))}
            <Lightformer
              intensity={2}
              rotation-y={Math.PI / 2}
              position={[-5, 1, -1]}
              scale={[50, 2, 1]}
            />
            <Lightformer
              intensity={2}
              rotation-y={Math.PI / 2}
              position={[-5, -1, -1]}
              scale={[50, 2, 1]}
            />
            <Lightformer
              intensity={2}
              rotation-y={-Math.PI / 2}
              position={[10, 1, 0]}
              scale={[50, 2, 1]}
            />
          </group>
        </Environment>
      </Suspense>
      <Grids />
      {/* <NavBlocks /> */}
      {/* <mesh
        ref={cubeRef}
        scale={0.5}
        position-x={2}
        rotation-y={Math.PI * 0.25}
        castShadow
      >
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshStandardMaterial color="#c77eff" />
      </mesh> */}
      {/* {!hovered && <DragIcon />} */}
      {/* Suspense for Text and Arrows to prevent blocking */}
      <Suspense fallback={null}>
        <Arrow3D
          rotation={[0, Math.PI / 2, Math.PI / 2]}
          position={[-9, 0.125, -1]}
          scale={[0.25, 1, 0.5]}
        />
        <Text3D
          rotation={[0, Math.PI / 2, 0]}
          position={[-11, 0, -2]}
          scale={0.2}
          color="#ff6c9d"
        >
          Arch
        </Text3D>
        <Arrow3D
          rotation={[0, -Math.PI, Math.PI / 2]}
          position={[-1, 0.125, -9]}
          scale={[0.25, 1, 0.5]}
        />
        <Text3D
          rotation={[0, 0, 0]}
          position={[-2.5, 0, -11]}
          scale={0.2}
          color="#dda0dd"
        >
          VArch
        </Text3D>
        <Arrow3D
          rotation={[0, Math.PI - Math.PI / 4, Math.PI / 2]}
          position={[-0, 0.125, -0]}
          scale={[0.25, 1, 0.5]}
        />
        <Text3D
          rotation={[0, Math.PI / 4, 0]}
          position={[3, 0, 3]}
          scale={0.2}
          color="#eaff7f"
        >
          Projects
        </Text3D>
        <Arrow3D
          rotation={[0, -Math.PI / 4, Math.PI / 2]}
          position={[-19, 0.125, -19]}
          scale={[0.25, 1, 0.5]}
        />
        <Text3D
          rotation={[0, Math.PI / 4, 0]}
          position={[-14.25, 0, -14.25]}
          scale={0.2}
          color="#00bfff"
        >
          Design
        </Text3D>
        <Text3D
          rotation={[0, Math.PI / 4, 0]}
          position={[-15.5, 0, -15.5]}
          scale={0.2}
          color="#00bfff"
        >
          Product
        </Text3D>
      </Suspense>
      {/* Separate Suspense boundaries for progressive loading */}
      <Suspense
        fallback={<Placeholder position={[0, 0.5, 0]} scale={[1, 1, 1]} />}
      >
        {/* <CaiLogoLamina position={[-2, -1, 2]} scale={2} castShadow /> */}
        <Center top position={[-height / 2 + 1, 0, -height / 2 + 1]}>
          <CaiLogo position={[0, 0, 0]} scale={1} castShadow />
        </Center>
      </Suspense>
      {/* ArchSection with Effects - each model has its own Suspense inside */}
      <Selection>
        <Effects />
        <ArchSection />
        <VArchSection />
        <ProductSection />
        <Suspense fallback={null}>
          <ProjectsSection />
        </Suspense>
      </Selection>
      {/* Avatar + Click-to-Move */}
      {showColliders && <DebugColliders waypoints={movement.waypoints} />}
      {showZones && <DebugZones />}
      <PathLine waypoints={movement.waypoints} avatarPosition={movement.position} />
      <GroundPlane
        onClickGround={(point) => {
          // While a project is focused, a ground click exits the focus view
          // instead of moving the avatar.
          const { focused, clearFocus } = useProjectFocus.getState();
          if (focused) {
            clearFocus();
            return;
          }
          movement.setTarget(point);
        }}
      />
      <Suspense fallback={null}>
        <Avatar position={movement.position} isMoving={movement.isMoving} onClick={toggleChat} />
        {/* Hidden during focus: it portals to document.body, and when the
            avatar falls behind the focus camera its projected position
            lands outside the viewport and stretches the page. */}
        {!focusBusy && <SpeechBubble avatarPosition={movement.position} />}
      </Suspense>
      {/* <TransformControls object={cubeRef} /> */}
      {/* <Clouds /> */}
    </>
  );
}
